import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { ProjectOption, normalizePlatformId, platformIdToProjectId, toProjectOption } from '../../constants/projects.constants';
import { safeReadJson, safeWriteJson } from '../../utils/storage';
import { environment } from '@environments/environment';

type StoredProjectContext = {
  selectedProjectId?: string | null;
  allowedPlatformIds?: string[];
};

@Injectable({
  providedIn: 'root'
})
export class ProjectContextService {
  private readonly STORAGE_KEY = 'everest.project-context.v1';

  private readonly allowedProjectsSubject = new BehaviorSubject<ProjectOption[]>([]);
  readonly allowedProjects$: Observable<ProjectOption[]> = this.allowedProjectsSubject.asObservable();

  private readonly selectedProjectIdSubject = new BehaviorSubject<string | null>(null);
  readonly selectedProjectId$: Observable<string | null> = this.selectedProjectIdSubject.asObservable();

  constructor() {
    this.restoreFromStorage();
    // In demo mode (E2E), keep a fallback project to keep navigation deterministic.
    if ((environment as any).authProvider === 'demo') {
      if (!this.selectedProjectIdSubject.value && typeof (environment as any).projectId === 'string') {
        this.selectedProjectIdSubject.next(String((environment as any).projectId));
      }
    }
  }

  getSelectedProjectIdSnapshot(): string | null {
    return this.selectedProjectIdSubject.value;
  }

  getAllowedProjectsSnapshot(): ProjectOption[] {
    return this.allowedProjectsSubject.value;
  }

  setAllowedPlatformIds(platformIds: string[]): void {
    const normalized = (platformIds || [])
      .map((id) => normalizePlatformId(id))
      .filter((id) => id.length > 0);

    const uniq = [...new Set(normalized)];
    const options = uniq.map((id) => toProjectOption(id));
    this.allowedProjectsSubject.next(options);

    // Ensure current selection is allowed (if we have an allowlist).
    const selected = this.selectedProjectIdSubject.value;
    if (options.length > 0) {
      const allowedProjectIds = new Set(options.map((o) => o.projectId));
      if (selected && !allowedProjectIds.has(selected)) {
        this.selectedProjectIdSubject.next(null);
      }

      if (!this.selectedProjectIdSubject.value && options.length === 1) {
        this.selectedProjectIdSubject.next(options[0].projectId);
      }
    }

    this.persist();
  }

  selectProjectByPlatformId(platformId: string): void {
    const projectId = platformIdToProjectId(platformId);
    this.selectProject(projectId);
  }

  selectProject(projectId: string): void {
    const normalized = (projectId || '').trim().toUpperCase();
    if (!normalized) {
      return;
    }

    // If allowlist exists, validate.
    const allowed = this.allowedProjectsSubject.value;
    if (allowed.length > 0 && !allowed.some((p) => p.projectId === normalized)) {
      return;
    }

    this.selectedProjectIdSubject.next(normalized);
    this.persist();
  }

  clear(): void {
    this.allowedProjectsSubject.next([]);
    this.selectedProjectIdSubject.next(null);
    this.persist();
  }

  private restoreFromStorage(): void {
    const stored = safeReadJson<StoredProjectContext>(this.STORAGE_KEY);
    if (!stored) {
      return;
    }

    if (Array.isArray(stored.allowedPlatformIds)) {
      this.allowedProjectsSubject.next(stored.allowedPlatformIds.map((id) => toProjectOption(id)));
    }

    if (typeof stored.selectedProjectId === 'string' && stored.selectedProjectId.trim().length > 0) {
      this.selectedProjectIdSubject.next(stored.selectedProjectId.trim().toUpperCase());
    }
  }

  private persist(): void {
    safeWriteJson(this.STORAGE_KEY, {
      selectedProjectId: this.selectedProjectIdSubject.value,
      allowedPlatformIds: this.allowedProjectsSubject.value.map((p) => p.platformId)
    } satisfies StoredProjectContext);
  }
}
